import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface ToggleSettingsState {
  // Session score state
  sessionScore: string;
  isEditingSessionScore: boolean;
  isSavingSessionScore: boolean;
  
  // Contact acquisition state
  maxContacts: string;
  maxBudget: string;
  isEditingContactAcquisition: boolean;
  isSavingContactAcquisition: boolean;
  
  // Actions
  setSessionScore: (value: string) => void;
  toggleSessionScoreEdit: () => void;
  resetSessionScore: (value: string) => void;
  setIsSavingSessionScore: (saving: boolean) => void;
  
  setMaxContacts: (value: string) => void;
  setMaxBudget: (value: string) => void;
  setContactValues: (maxContacts: string, maxBudget: string) => void;
  toggleContactAcquisitionEdit: () => void;
  resetContactAcquisition: (maxContacts: string, maxBudget: string) => void;
  setIsSavingContactAcquisition: (saving: boolean) => void;
  
  // Helper to reset all values
  resetAll: (sessionScore: string, maxContacts: string, maxBudget: string) => void;

  // Clears everything, for when the account changes
  reset: () => void;
}

const initialToggleSettingsState = {
  sessionScore: '',
  isEditingSessionScore: false,
  isSavingSessionScore: false,
  maxContacts: '',
  maxBudget: '',
  isEditingContactAcquisition: false,
  isSavingContactAcquisition: false,
};

export const useToggleSettingsStore = create<ToggleSettingsState>()(
  devtools(
    (set) => ({
      ...initialToggleSettingsState,
      
      // Session score actions
      setSessionScore: (value) => set({ sessionScore: value }),
      
      toggleSessionScoreEdit: () => 
        set((state) => ({ isEditingSessionScore: !state.isEditingSessionScore })),
      
      resetSessionScore: (value) => 
        set({ sessionScore: value, isEditingSessionScore: false, isSavingSessionScore: false }),
        
      setIsSavingSessionScore: (saving) => set({ isSavingSessionScore: saving }),
      
      // Contact acquisition actions
      setMaxContacts: (value) => set({ maxContacts: value }),
      
      setMaxBudget: (value) => set({ maxBudget: value }),
      
      setContactValues: (maxContacts, maxBudget) => 
        set({ maxContacts, maxBudget }),
      
      toggleContactAcquisitionEdit: () => 
        set((state) => ({ isEditingContactAcquisition: !state.isEditingContactAcquisition })),
      
      resetContactAcquisition: (maxContacts, maxBudget) => 
        set({ maxContacts, maxBudget, isEditingContactAcquisition: false, isSavingContactAcquisition: false }),
        
      setIsSavingContactAcquisition: (saving) => set({ isSavingContactAcquisition: saving }),
      
      // Reset all
      resetAll: (sessionScore, maxContacts, maxBudget) =>
        set({
          sessionScore,
          maxContacts,
          maxBudget,
          isEditingSessionScore: false,
          isEditingContactAcquisition: false,
          isSavingSessionScore: false,
          isSavingContactAcquisition: false,
        }),

      reset: () => set(initialToggleSettingsState),
    }),
    {
      name: 'toggle-settings-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);